import apiService from "../../../service/Api";
import type {
  ApplicationSubmission,
  CompetencyAnswerReviewResponse,
  CompetencyQuestionTemplate,
  FullCreateApplicationPayload,
  JobPosition,
} from "../types";
import { INITIAL_COMPETENCY_TEMPLATES } from "./mockData";

const COMPETENCY_TEMPLATES_STORAGE_KEY = "mms_competency_question_templates";

type PaginatedResponse<T> = { results: T[]; next: string | null };

function unwrapList<T>(data: T[] | PaginatedResponse<T>): T[] {
  return Array.isArray(data) ? data : data.results;
}

// The API paginates at 20/page server-side; the admin UI paginates
// client-side over the full list (10/page), so fetch every page up front.
export async function getJobs(): Promise<JobPosition[]> {
  const all: JobPosition[] = [];
  let url: string | null = "/api/job-career/jobs/";
  while (url) {
    const response: { data: JobPosition[] | PaginatedResponse<JobPosition> } = await apiService.get<
      JobPosition[] | PaginatedResponse<JobPosition>
    >(url);
    all.push(...unwrapList(response.data));
    url = Array.isArray(response.data) ? null : response.data.next;
  }
  return all;
}

export async function getJobById(id: string): Promise<JobPosition> {
  const response = await apiService.get<JobPosition>(`/api/job-career/jobs/${id}/`);
  return response.data;
}

export async function addJob(
  jobData: Omit<JobPosition, "id" | "createdAt" | "active">,
): Promise<JobPosition> {
  const response = await apiService.post<JobPosition>("/api/job-career/jobs/", {
    ...jobData,
    active: true,
  });
  return response.data;
}

export async function updateJob(
  id: string,
  jobData: Omit<JobPosition, "id" | "createdAt" | "active">,
): Promise<JobPosition> {
  const response = await apiService.patch<JobPosition>(`/api/job-career/jobs/${id}/`, jobData);
  return response.data;
}

export async function toggleJobActive(id: string, active: boolean): Promise<JobPosition> {
  const response = await apiService.patch<JobPosition>(`/api/job-career/jobs/${id}/`, { active });
  return response.data;
}

export function getCompetencyTemplates(): CompetencyQuestionTemplate[] {
  if (typeof window === "undefined") return INITIAL_COMPETENCY_TEMPLATES;
  const stored = localStorage.getItem(COMPETENCY_TEMPLATES_STORAGE_KEY);
  if (!stored) {
    localStorage.setItem(COMPETENCY_TEMPLATES_STORAGE_KEY, JSON.stringify(INITIAL_COMPETENCY_TEMPLATES));
    return INITIAL_COMPETENCY_TEMPLATES;
  }
  try {
    return JSON.parse(stored);
  } catch {
    return INITIAL_COMPETENCY_TEMPLATES;
  }
}

export function addCompetencyTemplate(
  templateData: Omit<CompetencyQuestionTemplate, "id" | "createdAt">,
): CompetencyQuestionTemplate {
  const templates = getCompetencyTemplates();
  const newTemplate: CompetencyQuestionTemplate = {
    ...templateData,
    id: `template-${Date.now()}`,
    createdAt: new Date().toISOString(),
  };
  const updated = [newTemplate, ...templates];
  if (typeof window !== "undefined") {
    localStorage.setItem(COMPETENCY_TEMPLATES_STORAGE_KEY, JSON.stringify(updated));
  }
  return newTemplate;
}

export function updateCompetencyTemplate(
  id: string,
  templateData: Omit<CompetencyQuestionTemplate, "id" | "createdAt">,
): CompetencyQuestionTemplate | null {
  const templates = getCompetencyTemplates();
  const index = templates.findIndex((t) => t.id === id);
  if (index === -1) return null;
  const updatedTemplate: CompetencyQuestionTemplate = { ...templates[index], ...templateData };
  templates[index] = updatedTemplate;
  if (typeof window !== "undefined") {
    localStorage.setItem(COMPETENCY_TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
  }
  return updatedTemplate;
}

export function deleteCompetencyTemplate(id: string): void {
  const templates = getCompetencyTemplates().filter((t) => t.id !== id);
  if (typeof window !== "undefined") {
    localStorage.setItem(COMPETENCY_TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
  }
}

export async function getSubmissions(): Promise<ApplicationSubmission[]> {
  const all: ApplicationSubmission[] = [];
  let url: string | null = "/api/job-career/applications/";
  while (url) {
    const response: { data: ApplicationSubmission[] | PaginatedResponse<ApplicationSubmission> } =
      await apiService.get<ApplicationSubmission[] | PaginatedResponse<ApplicationSubmission>>(url);
    all.push(...unwrapList(response.data));
    url = Array.isArray(response.data) ? null : response.data.next;
  }
  return all;
}

export async function fullCreateApplication(
  payload: FullCreateApplicationPayload,
  resume?: File,
  video?: File,
): Promise<ApplicationSubmission> {
  const formData = new FormData();
  formData.append("data", JSON.stringify(payload));
  if (resume) formData.append("resume", resume);
  if (video) formData.append("video", video);
  const response = await apiService.post<ApplicationSubmission>(
    "/api/job-career/applications/full-create/",
    formData,
  );
  return response.data;
}

export async function deleteApplicationVideo(id: string): Promise<void> {
  await apiService.delete(`/api/job-career/applications/${id}/video/`);
}

export async function updateSubmissionStatus(
  id: string,
  status: ApplicationSubmission["status"],
): Promise<ApplicationSubmission> {
  const response = await apiService.patch<ApplicationSubmission>(
    `/api/job-career/applications/${id}/review-status/`,
    { status },
  );
  return response.data;
}

export async function reviewCompetencyAnswer(
  applicationId: string,
  answerId: string,
  reviewPercentage: number,
  reviewFeedback: string,
): Promise<CompetencyAnswerReviewResponse> {
  const response = await apiService.patch<CompetencyAnswerReviewResponse>(
    `/api/job-career/applications/${applicationId}/competency-answers/${answerId}/review/`,
    { review_percentage: reviewPercentage, review_feedback: reviewFeedback },
  );
  return response.data;
}
